import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../hooks/useLanguage';

export default function AccessManagementPage() {
  const { token, user } = useAuth();
  const { t } = useLanguage();
  const [profiles, setProfiles] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profileModules, setProfileModules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    // Cargar perfiles y módulos en paralelo
    Promise.all([
      axios.get(`${API_BASE}/admin/profiles`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API_BASE}/admin/modules`, { headers: { Authorization: `Bearer ${token}` } })
    ])
      .then(([profilesRes, modulesRes]) => {
        setProfiles(profilesRes.data);
        setModules(modulesRes.data);
      })
      .catch(e => setError(e.response?.data?.detail || t('accessManagement.errorLoading')));
  }, [token]);

  const loadProfileModules = useCallback((pid) => {
    setSelectedProfile(pid);
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    axios.get(`${API_BASE}/admin/profiles/${pid}/modules`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setProfileModules(r.data.map(m => m.id)))
      .catch(e => setError(e.response?.data?.detail || t('accessManagement.errorLoadingModules')));
  }, [token]);

  const toggleModule = useCallback((mid) => {
    setProfileModules(prev => prev.includes(mid) ? prev.filter(x => x !== mid) : [...prev, mid]);
  }, []);

  const save = useCallback(() => {
    if (!selectedProfile) return;
    setSaving(true);
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    axios.post(
      `${API_BASE}/admin/profiles/${selectedProfile}/modules`,
      { module_ids: profileModules },
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then(() => loadProfileModules(selectedProfile))
      .catch(e => setError(e.response?.data?.detail || t('accessManagement.errorSaving')))
      .finally(() => setSaving(false));
  }, [selectedProfile, profileModules, token, loadProfileModules]);

  if (!user || user.id_perfil !== 1) {
    return <div>{t('accessManagement.unauthorized')}</div>;
  }

  return (
    <div className="container-fluid px-0">
      <h2 className="h4 mb-4">{t('accessManagement.title')}</h2>
      {error && <div className="alert alert-danger py-2">{error}</div>}
      <div className="row g-4">
        <div className="col-12 col-md-4">
          <div className="card h-100 shadow-sm">
            <div className="card-body">
              <h3 className="h6 text-uppercase text-muted mb-3">{t('accessManagement.profiles')}</h3>
              <div className="list-group small">
                {profiles.map(p => (
                  <button
                    key={p.id}
                    onClick={() => loadProfileModules(p.id)}
                    className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${selectedProfile === p.id ? 'active' : ''}`}
                  >
                    <span>{p.descripcion || t('accessManagement.profile', { id: p.id })}</span>
                    {selectedProfile === p.id && <span className="badge bg-light text-primary">{t('accessManagement.active')}</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-8">
          <div className="card h-100 shadow-sm">
            <div className="card-body d-flex flex-column">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="h6 text-uppercase text-muted mb-0">{t('accessManagement.modules')}</h3>
                {selectedProfile && (
                  <button onClick={save} disabled={saving} className="btn btn-sm btn-primary">
                    {saving ? t('accessManagement.saving') : t('accessManagement.saveChanges')}
                  </button>
                )}
              </div>
              {!selectedProfile && <p className="text-muted small mb-0">{t('accessManagement.selectProfile')}</p>}
              {selectedProfile && (
                <div className="table-responsive" style={{maxHeight:'50vh'}}>
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th style={{width:'55px'}}>{t('accessManagement.access')}</th>
                        <th>{t('common.description', { defaultValue: 'Descripción' })}</th>
                        <th>URL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modules.map(m => (
                        <tr key={m.id}>
                          <td>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={profileModules.includes(m.id)}
                              onChange={() => toggleModule(m.id)}
                            />
                          </td>
                          <td className="small">{m.descripcion}</td>
                          <td className="text-muted small">{m.url}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}